import createReducer, { ActionCreatorHelper } from 'store/createReducer';

export enum CampaignFilter {
    ProductTypes = 'productTypes',
    CampaignStatuses = 'campaignStatuses',
    BudgetStatuses = 'budgetStatuses',
    ModerationStatuses = 'moderationStatuses',
    Areas = 'areas',
    ProfessionalRoles = 'professionalRoles',
    CreatedByUserIds = 'createdByUserIds',
}

export interface AppliedCampaignsFilters {
    [CampaignFilter.ProductTypes]: Set<string>;
    [CampaignFilter.CampaignStatuses]: Set<string>;
    [CampaignFilter.BudgetStatuses]: Set<string>;
    [CampaignFilter.ModerationStatuses]: Set<string>;
    [CampaignFilter.Areas]: Set<string>;
    [CampaignFilter.ProfessionalRoles]: Set<string>;
    [CampaignFilter.CreatedByUserIds]: Set<string>;
}

export interface CampaignsFilters {
    [CampaignFilter.ProductTypes]?: Record<string, string>;
    [CampaignFilter.CampaignStatuses]?: Record<string, string>;
    [CampaignFilter.BudgetStatuses]?: Record<string, string>;
    [CampaignFilter.ModerationStatuses]?: Record<string, string>;
    [CampaignFilter.Areas]?: Record<string, string>;
    [CampaignFilter.ProfessionalRoles]?: Record<string, string>;
    [CampaignFilter.CreatedByUserIds]?: Record<string, string>;
}

export enum ColumnsPreset {
    Important = 'important',
    All = 'all',
}

// ----------------------- actions -------------------------------------
const SET_CAMPAIGNS_FILTERS = 'SET_CAMPAIGNS_FILTERS';
const SET_IS_CAMPAIGNS_FILTERS_LOADING = 'SET_IS_CAMPAIGNS_FILTERS_LOADING';
const SET_CAMPAIGNS_SEARCH_VALUE = 'SET_CAMPAIGNS_SEARCH_VALUE';
const SET_APPLIED_CAMPAIGNS_FILTERS = 'SET_APPLIED_CAMPAIGNS_FILTERS';
const SET_APPLIED_CAMPAIGNS_FILTERS_TOTAL_COUNT = 'SET_APPLIED_CAMPAIGNS_FILTERS_TOTAL_COUNT';
const SET_CAMPAIGNS_FILTERS_DRAFT = 'SET_CAMPAIGNS_FILTERS_DRAFT';
const SET_COLUMNS_PRESET = 'SET_COLUMNS_PRESET';

interface PayloadTypes {
    [SET_CAMPAIGNS_FILTERS]: CampaignsFilters;
    [SET_IS_CAMPAIGNS_FILTERS_LOADING]: boolean;
    [SET_CAMPAIGNS_SEARCH_VALUE]: string;
    [SET_APPLIED_CAMPAIGNS_FILTERS]: AppliedCampaignsFilters;
    [SET_APPLIED_CAMPAIGNS_FILTERS_TOTAL_COUNT]: number;
    [SET_CAMPAIGNS_FILTERS_DRAFT]: AppliedCampaignsFilters;
    [SET_COLUMNS_PRESET]: ColumnsPreset;
}

const ActionCreator = ActionCreatorHelper<PayloadTypes>();

export const setCampaignsFilters = ActionCreator(SET_CAMPAIGNS_FILTERS);
export const setIsCampaignsFiltersLoading = ActionCreator(SET_IS_CAMPAIGNS_FILTERS_LOADING);
export const setCampaignsFiltersSearchValue = ActionCreator(SET_CAMPAIGNS_SEARCH_VALUE);
export const setAppliedCampaignsFilters = ActionCreator(SET_APPLIED_CAMPAIGNS_FILTERS);
export const setAppliedCampaignsFiltersTotalCount = ActionCreator(SET_APPLIED_CAMPAIGNS_FILTERS_TOTAL_COUNT);
export const setCampaignsFiltersDraft = ActionCreator(SET_CAMPAIGNS_FILTERS_DRAFT);
export const setColumnsPreset = ActionCreator(SET_COLUMNS_PRESET);
// ----------------------- /actions -------------------------------------

// ----------------------- reducer --------------------------------------
interface CampaignsFiltersState {
    campaignsFilters: CampaignsFilters;
    isCampaignsFiltersLoading: boolean;
    campaignsSearchValue: string;
    appliedCampaignsFilters: AppliedCampaignsFilters;
    appliedCampaignsFiltersTotalCount: number;
    campaignsFiltersDraft: AppliedCampaignsFilters;
    columnsPreset: ColumnsPreset;
}

export const INITIAL_APPLIED_CAMPAIGNS_FILTERS_STATE = {
    productTypes: new Set<string>(),
    campaignStatuses: new Set<string>(),
    budgetStatuses: new Set<string>(),
    moderationStatuses: new Set<string>(),
    areas: new Set<string>(),
    professionalRoles: new Set<string>(),
    createdByUserIds: new Set<string>(),
};

export const CAMPAIGNS_FILTERS_INITIAL_STATE: CampaignsFiltersState = {
    campaignsFilters: {
        productTypes: {},
        campaignStatuses: {},
        budgetStatuses: {},
        moderationStatuses: {},
        areas: {},
        professionalRoles: {},
        createdByUserIds: {},
    },
    isCampaignsFiltersLoading: false,
    campaignsSearchValue: '',
    appliedCampaignsFilters: { ...INITIAL_APPLIED_CAMPAIGNS_FILTERS_STATE },
    appliedCampaignsFiltersTotalCount: 0,
    campaignsFiltersDraft: { ...INITIAL_APPLIED_CAMPAIGNS_FILTERS_STATE },
    columnsPreset: ColumnsPreset.Important,
};

export default createReducer<CampaignsFiltersState, PayloadTypes>(CAMPAIGNS_FILTERS_INITIAL_STATE, {
    [SET_CAMPAIGNS_FILTERS]: (state, { payload }) => ({
        ...state,
        campaignsFilters: payload,
    }),
    [SET_IS_CAMPAIGNS_FILTERS_LOADING]: (state, { payload }) => ({
        ...state,
        isCampaignsFiltersLoading: payload,
    }),
    [SET_CAMPAIGNS_SEARCH_VALUE]: (state, { payload }) => ({
        ...state,
        campaignsSearchValue: payload,
    }),
    [SET_APPLIED_CAMPAIGNS_FILTERS]: (state, { payload }) => ({
        ...state,
        appliedCampaignsFilters: payload,
    }),
    [SET_APPLIED_CAMPAIGNS_FILTERS_TOTAL_COUNT]: (state, { payload }) => ({
        ...state,
        appliedCampaignsFiltersTotalCount: payload,
    }),
    [SET_CAMPAIGNS_FILTERS_DRAFT]: (state, { payload }) => ({
        ...state,
        campaignsFiltersDraft: payload,
    }),
    [SET_COLUMNS_PRESET]: (state, { payload }) => ({
        ...state,
        columnsPreset: payload,
    }),
});
// ----------------------- /reducer --------------------------------------
